/**
 * 认证服务 - OAuth2 版本
 * 支持 OAuth2 Bearer Token 认证流程
 */

import api from './api';
import endpoints from './endpoints';
import oneauthApi from './oneauthApi';
import oauth2Service from './oauth2Service';
import { setLoggingOut } from './tokenSyncService';

// 判断是否使用本地模拟数据
const VITE_USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Token 存储键名
const TOKEN_KEY = 'syntrust_access_token';
const LEGACY_TOKEN_KEY = 'syntrust_auth_token';
const REFRESH_TOKEN_KEY = 'syntrust_refresh_token';
const USER_KEY = 'syntrust_user';
const TOKEN_EXPIRES_KEY = 'syntrust_token_expires';
const AUTH_STATUS = 'syntrust_login_status';

const isLikelyUrl = (value) => {
  if (typeof value !== 'string') return false;
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/') ||
    value.startsWith('data:')
  );
};

const joinUrl = (base, path) => {
  const safeBase = (base || '').replace(/\/$/, '');
  const safePath = (path || '').replace(/^\//, '');
  if (!safeBase) return `/${safePath}`;
  return `${safeBase}/${safePath}`;
};

const normalizeUser = (user) => {
  if (!user || typeof user !== 'object') return user;

  const normalized = { ...user };
  const avatarUrlCandidate = normalized.avatar_url || normalized.avatarUrl;
  const avatarFromAvatarUrl = isLikelyUrl(avatarUrlCandidate) ? avatarUrlCandidate : null;

  const avatarCandidate = normalized.avatar;
  const avatarFromAvatarField = isLikelyUrl(avatarCandidate) ? avatarCandidate : null;

  const avatarIdCandidate = normalized.avatar_id || normalized.avatarId;
  const apiBase = import.meta.env.VITE_API_BASE_URL || '';
  const avatarFromId = avatarIdCandidate ? joinUrl(apiBase, `/contents/${avatarIdCandidate}/file`) : null;

  const resolvedAvatar = avatarFromAvatarUrl || avatarFromAvatarField || avatarFromId;
  if (resolvedAvatar) {
    normalized.avatar = resolvedAvatar;
    if (!normalized.avatar_url) normalized.avatar_url = resolvedAvatar;
  }

  return normalized;
};

// Token 自动刷新定时器
let refreshTimer = null;

/**
 * 保存认证信息
 * @param {Object} authData - 认证数据，包含 access_token、refresh_token、user 等
 */
export const saveAuth = (authData) => {
  console.log('Saving auth data:', authData);

  // OAuth2 模式
  if (authData.access_token) {
    localStorage.setItem(TOKEN_KEY, authData.access_token);
    localStorage.setItem(LEGACY_TOKEN_KEY, authData.access_token);
  }

  if (authData.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, authData.refresh_token);
  }

  if (authData.expires_in) {
    const expiresAt = Date.now() + authData.expires_in * 1000;
    localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt.toString());
    
    // 调度 token 刷新
    scheduleTokenRefresh(authData.expires_in);
  }

  // 兼容旧的 session token 模式
  if (authData.token) {
    localStorage.setItem(TOKEN_KEY, authData.token);
    localStorage.setItem(LEGACY_TOKEN_KEY, authData.token);
  }

  if (authData.user) {
    authData.user = normalizeUser(authData.user);
    // 自动补充 inserted_at 和 expires_in 字段（向后兼容）
    if (!authData.user.inserted_at) {
      authData.user.inserted_at = Date.now();
    }
    if (!authData.user.expires_in) {
      authData.user.expires_in = authData.expires_in || 1000 * 60 * 60 * 24 * 7; // 默认 7 天
    }
    localStorage.setItem(USER_KEY, JSON.stringify(authData.user));
  }

  // 设置登录状态
  localStorage.setItem(AUTH_STATUS, 'true');
};

/**
 * 获取认证 Token (access_token 或 session token)
 * @returns {string|null} - Token 或 null
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
};

/**
 * 获取刷新令牌
 * @returns {string|null} - Refresh Token 或 null
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * 检查 token 是否即将过期（5 分钟内）
 * @returns {boolean}
 */
export const isTokenExpiringSoon = () => {
  const expiresAt = parseInt(localStorage.getItem(TOKEN_EXPIRES_KEY) || '0');
  if (!expiresAt) return false;

  const now = Date.now();
  const timeUntilExpiry = expiresAt - now;

  // 如果在 5 分钟内过期，返回 true
  return timeUntilExpiry < 5 * 60 * 1000;
};

/**
 * 获取当前用户信息
 * @returns {Object|null} - 用户信息或 null
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('解析用户信息失败:', error);
    return null;
  }
};

/**
 * 检查是否已认证
 * @returns {boolean} - 是否已认证
 */
export const isAuthenticated = () => {
  const hasToken = !!getToken();
  const authStatus = localStorage.getItem(AUTH_STATUS) === 'true';
  return authStatus || hasToken;
};

/**
 * 清除认证信息
 */
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_KEY);
  localStorage.removeItem(AUTH_STATUS);

  // 清除刷新定时器
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * 使用 Kratos 登录流程
 * @deprecated 此方法已废弃，请使用 OAuth2 流程 (oauth2Service.initiateOAuth2Login)
 * @param {string} returnUrl - 登录成功后返回的 URL
 * @returns {Promise<Object>} - 登录流程对象
 */
export const loginWithKratos = async (returnUrl = '/') => {
  console.warn('loginWithKratos is deprecated. Please use OAuth2 flow with oauth2Service.initiateOAuth2Login');
  try {
    console.log('Initiating Kratos login flow...');

    // 创建 Kratos 登录流程
    const flow = await oneauthApi.kratos.createLoginFlow(returnUrl);

    console.log('Kratos login flow created:', flow.id);

    return flow;
  } catch (error) {
    console.error('Failed to create Kratos login flow:', error);
    throw error;
  }
};

/**
 * 提交 Kratos 登录表单
 * @deprecated 此方法已废弃，请使用 OAuth2 流程
 * @param {string} flowId - 流程 ID
 * @param {object} credentials - 登录凭证 (identifier, password, method 等)
 * @returns {Promise<Object>} - 登录结果
 */
export const submitKratosLogin = async (flowId, credentials) => {
  console.warn('submitKratosLogin is deprecated. Please use OAuth2 flow');
  try {
    console.log('Submitting Kratos login...');

    const response = await oneauthApi.kratos.submitLogin(flowId, {
      method: credentials.method || 'password',
      identifier: credentials.identifier,
      password: credentials.password,
      csrf_token: credentials.csrf_token,
    });

    console.log('Kratos login successful');

    // 获取 session 信息
    const session = await oneauthApi.kratos.getSession();

    // 从 Kratos session 中提取用户信息并保存
    const user = {
      id: session.identity.id,
      email: session.identity.traits.email,
      username: session.identity.traits.username || session.identity.traits.email,
      name: session.identity.traits.name?.first || session.identity.traits.username,
      traits: session.identity.traits,
    };

    // 保存认证信息（Kratos 使用 cookie，不需要保存 token）
    saveAuth({
      user,
      // Kratos 使用 session cookie，不返回 access_token
      // 但我们可以标记为已认证
      session_id: session.id,
    });

    return { success: true, user, session };
  } catch (error) {
    console.error('Kratos login failed:', error);
    throw error;
  }
};

/**
 * 检查 Kratos 会话状态
 * @deprecated 此方法已废弃，OAuth2 流程使用 token 而非 session
 * @returns {Promise<Object|null>} - 会话信息或 null
 */
export const checkKratosSession = async () => {
  console.warn('checkKratosSession is deprecated in OAuth2 flow');
  try {
    const session = await oneauthApi.kratos.getSession();

    if (session && session.active) {
      // 更新本地用户信息
      const user = {
        id: session.identity.id,
        email: session.identity.traits.email,
        username: session.identity.traits.username || session.identity.traits.email,
        name: session.identity.traits.name?.first || session.identity.traits.username,
        traits: session.identity.traits,
      };

      setCurrentUser(user);
      localStorage.setItem(AUTH_STATUS, 'true');

      return session;
    }

    return null;
  } catch (error) {
    console.log('No active Kratos session');
    return null;
  }
};

/**
 * 使用 Kratos 登出
 * @deprecated 此方法已废弃，请使用 oauth2Service.revokeToken
 * @returns {Promise<void>}
 */
export const logoutWithKratos = async () => {
  console.warn('logoutWithKratos is deprecated. Please use oauth2Service.revokeToken');
  try {
    console.log('Logging out with Kratos...');

    // 创建登出流程
    const logoutFlow = await oneauthApi.kratos.createLogoutFlow();

    // 执行登出
    await oneauthApi.kratos.logout(logoutFlow.logout_token);

    // 清除本地认证信息
    clearAuth();

    // 跳转到登录页
    window.location.replace('/');
  } catch (error) {
    console.error('Kratos logout failed:', error);
    // 即使失败也清除本地信息
    clearAuth();
    window.location.replace('/');
  }
};

/**
 * 发起 OAuth2 登录流程（旧方法，已废弃）
 * @deprecated 使用 loginWithKratos 替代
 * @param {string} returnUrl - 登录成功后返回的 URL
 */
export const loginWithOAuth = async (returnUrl = '/') => {
  console.warn('loginWithOAuth is deprecated, use loginWithKratos instead');
  return loginWithKratos(returnUrl);
};

/**
 * 处理 OAuth 回调
 * @param {string} code - 授权码
 * @param {string} state - 状态参数
 * @returns {Promise<Object>} - 包含 user 的对象
 */
export const handleOAuthCallback = async (code, state) => {
  try {
    console.log('Handling OAuth callback...', { code: code?.slice(0, 10), state });

    // 1. 验证 state（防止 CSRF 攻击）
    const savedState = sessionStorage.getItem('oauth_state');
    if (state !== savedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // 2. 用授权码交换 token
    const data = await api.post(endpoints.auth.oauth.callback, {
      code,
      state
    });

    console.log('OAuth callback response:', data);

    // 3. 保存认证信息
    saveAuth(data);

    // 4. 清除 state
    sessionStorage.removeItem('oauth_state');

    return { success: true, user: data.user };
  } catch (error) {
    console.error('OAuth callback failed:', error);
    throw error;
  }
};

/**
 * Hydra OAuth2 登录
 * 直接调用 oauth2Service.initiateOAuth2Login
 */
export const loginWithOAuth2 = async (returnUrl = '/') => {
  console.log('🚀 Initiating OAuth2 login...');
  return oauth2Service.initiateOAuth2Login(returnUrl);
};

/**
 * 处理 OAuth2 回调 (新)
 */
export const handleOAuth2Callback = async (code, state) => {
  try {
    // 1. 交换 Token
    const tokenData = await oauth2Service.handleOAuth2Callback(code, state);

    // 2. 保存 Token (必须先保存，因为后续 API 调用需要 token)
    saveAuth(tokenData);

    // 3. 获取用户信息
    const user = await fetchUserInfo();

    // 4. 更新用户信息
    const mergedAuthData = { ...tokenData, user };
    saveAuth(mergedAuthData);

    return mergedAuthData;

  } catch (error) {
    console.error('OAuth2 callback failed:', error);
    throw error;
  }
};

/**
 * 获取用户信息
 * 优先使用后端 API，如果失败则尝试从 Kratos 获取
 */
async function fetchUserInfo() {
  try {
    // 优先使用后端 /auth/me API (使用 access_token)
    const response = await api.get(endpoints.auth.me);
    if (response && response.user) {
      return normalizeUser(response.user);
    }
    return normalizeUser(response);
  } catch (error) {
    console.warn('Failed to fetch user from backend API, trying Kratos:', error);

    // 降级方案：从 Kratos session 获取
    try {
      const session = await oneauthApi.kratos.getSession();
      return {
        id: session.identity.id,
        email: session.identity.traits.email,
        username: session.identity.traits.username || session.identity.traits.email,
        name: session.identity.traits.name?.first || session.identity.traits.username,
        traits: session.identity.traits,
      };
    } catch (kratosError) {
      console.error('Failed to fetch user info from both sources:', kratosError);
      throw new Error('Unable to fetch user information');
    }
  }
}

/**
 * OAuth2 登出 - 两步流程
 * 1. POST 撤销 OAuth2 tokens（安全）
 * 2. 清除本地存储
 * 3. 重定向撤销 Kratos session
 */
export const logoutWithOAuth2 = async () => {
  // 设置登出标志，阻止 tokenSync 自动获取新 token
  setLoggingOut(true);

  const accessToken = getToken();
  const refreshToken = getRefreshToken();

  // 1. POST 撤销 OAuth2 tokens（在清除本地存储前）
  await oauth2Service.revokeOAuth2Tokens(accessToken, refreshToken);

  // 2. 清除本地存储
  clearAuth();

  // 3. 重定向撤销 Kratos session
  oauth2Service.redirectToLogout(window.location.origin);
};

/**
 * 刷新 Token
 * @returns {Promise<Object>} - 新的 token 信息
 */
export const refreshToken = async () => {
  const refresh_token = getRefreshToken();

  if (!refresh_token) {
    throw new Error('No refresh token available');
  }

  // 使用 OAuth2 刷新 Token
  try {
    console.log('🔄 Refreshing access token via OAuth2...');

    const tokenData = await oauth2Service.refreshAccessToken(refresh_token);

    // 保存新的 Token
    saveAuth({
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      expires_in: tokenData.expires_in,
      user: getCurrentUser(), // 保留当前用户信息
    });

    console.log('✅ Token refresh successful');
    return tokenData;

  } catch (error) {
    console.error('❌ OAuth2 token refresh failed:', error);
    clearAuth();
    throw error;
  }
};

/**
 * 定时刷新 token（在过期前 5 分钟）
 * @param {number} expiresIn - Token 有效期（秒）
 */
const scheduleTokenRefresh = (expiresIn) => {
  // 清除之前的定时器
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  // 在过期前 5 分钟刷新
  const refreshTime = (expiresIn - 300) * 1000; // 转换为毫秒

  if (refreshTime > 0) {
    console.log(`Scheduling token refresh in ${refreshTime / 1000} seconds`);

    refreshTimer = setTimeout(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Auto refresh failed:', error);
        clearAuth();
        window.location.replace('/');
      }
    }, refreshTime);
  }
};

/**
 * 登出
 */
export const logout = async () => {
  console.log('Logging out...');
  // 统一走 OneAuth 登出（两步流程）
  await logoutWithOAuth2();
};

/**
 * 获取 SSO 登录跳转地址
 * @deprecated 此方法已完全废弃，请直接使用 oauth2Service.initiateOAuth2Login
 * 保留此函数仅为防止旧代码调用时报错
 */
export const getSSOLoginUrl = async () => {
  console.error('❌ getSSOLoginUrl is deprecated. Use oauth2Service.initiateOAuth2Login directly.');
  throw new Error('This method is deprecated. Please use OAuth2 flow by calling initiateOAuth2Login directly.');
};

/**
 * 传统登录（用户名密码）- 向后兼容
 * @param {Object} credentials - 登录凭证，包含 username 和 password
 * @returns {Promise} - 登录结果
 */
export const login = async (credentials) => {
  // Mock 模式处理
  if (VITE_USE_MOCK_DATA) {
    return new Promise((resolve) => {
      const mockUserSig = 'mock_user_signature';
      const mockResponse = {
        token: 'mock_jwt_token',
        user: {
          id: credentials.username || 'mock-user',
          user_id: credentials.username || 'mock-user',
          name: credentials.username || 'mock用户',
          email: credentials.username || 'mock@example.com',
          role: 'user',
          workspace: '',
          user_signature: mockUserSig,
          user_sig: mockUserSig,
        },
        sidebar_list: {},
        user_signature: mockUserSig,
        user_sig: mockUserSig,
        workspace: ''
      };
      saveAuth(mockResponse);
      resolve(mockResponse);
    });
  }

  // 真实后端登录
  try {
    const response = await api.post('/login', credentials);
    saveAuth(response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * 设置当前用户信息
 * @param {Object} user - 用户信息对象
 */
export const setCurrentUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(normalizeUser(user)));
};

/**
 * 初始化认证服务
 * 检查 token 是否需要刷新，并设置自动刷新
 */
export const initAuth = () => {
  const token = getToken();
  const expiresAt = parseInt(localStorage.getItem(TOKEN_EXPIRES_KEY) || '0');

  if (token && expiresAt) {
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    if (timeUntilExpiry > 0) {
      // Token 仍然有效，计算剩余有效期并设置刷新
      const expiresIn = Math.floor(timeUntilExpiry / 1000);
      scheduleTokenRefresh(expiresIn);
      console.log(`Token valid for ${expiresIn} seconds`);
    } else {
      // Token 已过期，尝试刷新
      console.log('Token expired, attempting refresh...');
      refreshToken().catch((error) => {
        console.error('Token refresh failed on init:', error);
        clearAuth();
      });
    }
  }
};

// 导出
export default {
  // Kratos 方法
  loginWithKratos,
  submitKratosLogin,
  checkKratosSession,
  logoutWithKratos,

  // Hydra OAuth2 方法 (新)
  loginWithOAuth2,
  handleOAuth2Callback,
  logoutWithOAuth2,

  // 通用方法
  saveAuth,
  getToken,
  getRefreshToken,
  getCurrentUser,
  isAuthenticated,
  isTokenExpiringSoon,
  clearAuth,
  setCurrentUser,
  initAuth,
  refreshToken,

  // 旧的OAuth2 方法（已废弃，保留兼容）
  loginWithOAuth,
  handleOAuthCallback,
  logout,
  getSSOLoginUrl,
  login,
};
